import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

export interface IMacaddrModel {
  /**
   * MACアドレス
   */
  macaddr: string;
  /**
   * メモ
   */
  memo: string;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIから登録済みライセンスの取得が成功した場合に呼び出されます
   * @callback success
   * @param {number} max - 登録可能なシリアル番号の数
   * @param {number} registerd - 既に登録したシリアル番号の数
   * @param {string[]} codes - 登録したライセンスコードの配列
   * @param {IMacaddrModel[]} macaddrs - 登録したレコーダのシリアル番号の配列
   * @param {string} termcode - ご利用ライセンスコード
   * @param {string} termexpiration - ご利用満了日
   * @param {number} status - httpステータス
   */
  success: (
    max: number,
    registerd: number,
    codes: string[],
    macaddrs: IMacaddrModel[],
    termcode: string,
    termexpiration: string,
    status: number
  ) => void;
}

export interface IGetLicencesResponceModel {
  /**
   * 登録可能なシリアル番号の数
   */
  max: number;
  /**
   * 既に登録したシリアル番号の数
   */
  registerd: number;
  /**
   * 登録したライセンスコードの配列
   */
  codes: string[];
  /**
   * 登録したMACアドレス番号の配列
   */
  macaddrs: IMacaddrModel[];
  /**
   * ご利用ライセンスコード
   */
  termcode: string;
  /**
   * ご利用満了日
   */
  termexpiration: string;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.get(url);
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const data: IGetLicencesResponceModel = res.data;
      params.success(
        data.max,
        data.registerd,
        data.codes ? data.codes : [],
        data.macaddrs ? data.macaddrs : [],
        data.termcode ? data.termcode : "",
        data.termexpiration ? data.termexpiration : "",
        res.status
      );
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetLicencesParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * 登録済みライセンスを取得します
 * @function getLicences
 * @param {IGetLicencesParams} params - 登録済みライセンス取得パラメータ
 */
export function getLicences(params: IGetLicencesParams): void {
  const { authService } = params;
  //const url = WebAPI.LICENCES;
  const url = WebAPI.LICENCES;
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getLicencesRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * 登録済みライセンスを取得します
 * @function getLicencesRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getLicencesRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
